import { countriesOptions } from "./options";
const CryptoJS = require("crypto-js");

// Make first Word Uppercase
export const formatLabel = (value) => {
    if (!value) return "";
    return value
        .replace(/_/g, " ")
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};
// Auto-Capitalize on Space
export const capitalizeWords = (value) => {
    if (!value) return "";
    return value
        .split(" ")
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
};

// Date & Time format
export const formatMonthYear = (month, year) => {
    if (!month || !year) return "-";

    return new Date(year, month - 1).toLocaleString("en-US", {
        month: "short",
    }) + `-${year}`;
};
export const formatDateTime = (dateString) => {
    const date = new Date(dateString);

    const datePart = date.toLocaleDateString(undefined, {
        timeZone: "Asia/Karachi",
        weekday: "short", // Fri
        day: "2-digit",   // 25
        month: "short",   // Oct
        year: "numeric"   // 2025
    });

    const timePart = date.toLocaleTimeString(undefined, {
        timeZone: "Asia/Karachi",
        hour: "2-digit",
        minute: "2-digit",
    });

    return `${datePart} · ${timePart}`;
};
export const formatTableDateTime = (date) => {
    if (!date) return "—";
    const d = new Date(date);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, { timeZone: "Asia/Karachi" });
};
export const toDateTimeLocal = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";

    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Karachi",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    }).formatToParts(d);

    const get = (type) => parts.find(p => p.type === type)?.value;
    return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
};
export const toDateTimeIso = (value) => {
    if (!value) return null;

    const d = new Date(value);
    if (isNaN(d.getTime())) return null;

    return d.toISOString();
};

// Phone format validator & split [+92 / 3001234567]
export const isValidPhone = (input, countryCode) => {
    if (!input || !countryCode) return false;
    const country = countriesOptions.find(
        (c) => c.code === countryCode
    );
    if (!country) return false;
    const digits = input.replace(/\D/g, "");
    return digits.length === country.maxLength;
};
export const splitPhone = (fullPhone) => {
    if (!fullPhone) {
        return { countryCode: "PK", phone: "" };
    }
    const digits = fullPhone.replace(/\D/g, "");
    const match = countriesOptions.find((c) =>
        digits.startsWith(c.dial.replace(/\D/g, ""))
    );
    if (!match) {
        return { countryCode: "PK", phone: digits };
    }
    const dialDigits = match.dial.replace(/\D/g, "");
    return {
        countryCode: match.code,
        phone: digits.slice(dialDigits.length),
    };
};

// CNIC # format & validator 
export const formatCnic = (input) => {
    if (!input) return "";
    // Keep only digits
    let digits = input.replace(/\D/g, "");
    // Limit to 13 digits
    if (digits.length > 13) digits = digits.slice(0, 13);
    // Format with dashes
    if (digits.length <= 5) return digits;
    if (digits.length <= 12) return digits.slice(0, 5) + "-" + digits.slice(5);
    return digits.slice(0, 5) + "-" + digits.slice(5, 12) + "-" + digits.slice(12);
};
export const isValidCnic = (input) => {
    const digits = input.replace(/\D/g, "");
    return digits.length === 13;
};

// Formatter for XX & XX, XX... pattern
export const formatXX = (value) => {
    if (!value) return "";
    const digits = value.toString().replace(/\D/g, "").slice(0, 2);
    if (!digits) return "";
    return `${digits}`;
};
export const formatXXSets = (value, maxSets = 10) => {
    if (!value) return "";

    // keep digits only
    const digits = value.replace(/\D/g, "");

    // split into 2-digit chunks
    const chunks = digits.match(/\d{1,2}/g) || [];

    // limit number of sets
    const limited = chunks.slice(0, maxSets);

    // join as "XX, XX"
    return limited.join(", ");
};
export const formatPhoneSets = (value, maxSets = 3) => {
    if (!value) return "";
    // keep digits only
    const digits = value.replace(/\D/g, "");

    // split into 11-digit chunks
    const chunks = digits.match(/\d{1,11}/g) || [];

    // limit number of sets
    const limited = chunks.slice(0, maxSets);

    // join as "XXXXXXXXXXX, XXXXXXXXXXX"
    return limited.join(", ");
};

// Encrypt & Decrypt
export const encryptPayload = (data) => {
    try {
        if (!data) return null;
        let payload = data;
        // If it's a Mongoose document, convert to plain object
        if (typeof data === "object" && typeof data.toObject === "function") {
            payload = data.toObject();
        }
        // Stringify if not already string
        const stringData =
            typeof payload === "string"
                ? payload
                : JSON.stringify(payload);

        return CryptoJS.AES.encrypt(stringData, process.env.REACT_APP_SECURE_KEY).toString();
    } catch (error) {
        return null;
    }
}
export const decryptPayload = (encryptedData) => {
    try {
        if (!encryptedData) return null;

        const bytes = CryptoJS.AES.decrypt(encryptedData, process.env.REACT_APP_SECURE_KEY);
        const decryptedText = bytes.toString(CryptoJS.enc.Utf8);

        if (!decryptedText) return null;

        return JSON.parse(decryptedText);
    } catch (error) {
        return null;
    }
};

// Currency Formatter
export const formatCurrency = (value) => {
    if (!value) return "";
    return Number(value).toLocaleString("en-PK");
};

// Image Validator for Employee (Add/Update)
const ALLOWED_IMAGE_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/webp",
];
const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20 MB

export const validateImageFiles = (files) => {
    const valid = [];
    const errors = [];

    Array.from(files || []).forEach((file) => {
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
            errors.push("Only JPG/JPEG, PNG, or WEBP images allowed");
            return;
        }
        if (file.size > MAX_IMAGE_SIZE) {
            errors.push("File size must not exceed 20 MB");
            return;
        }
        valid.push(file);
    });

    return { valid, errors };
};